import { useState } from 'react';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { SearchIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SearchInput } from '@/shared/ui';

/**
 * Names the rail and holds the one thing you can do to the whole of it.
 *
 * The field is folded behind an icon because this column is 300px wide and the folders are the point
 * of it: a box permanently across the top spends a row of that width on a control most arrivals
 * never touch. Opened, it covers the header instead of pushing it down, so nothing below it moves
 * and the button that closes it lands exactly where the one that opened it was.
 */
export const RailHeader = ({
  title,
  search,
  onSearchChange,
}: {
  title: string;
  search: string;
  onSearchChange: (search: string) => void;
}) => {
  const { t } = useTranslation('tools');
  const [searching, setSearching] = useState(false);

  /**
   * Closing clears the term as well as the field. A rail still narrowed by words that are no longer
   * on screen reads as a rail that has lost rows, and the only way back is to reopen a control that
   * no longer looks like it is doing anything.
   */
  const close = () => {
    onSearchChange('');
    setSearching(false);
  };

  return (
    // Its own provider rather than the app's, for the reason FolderRail carries one: this rail is
    // mounted by more than one host, and a tooltip without a provider throws rather than degrading.
    <TooltipProvider>
      <div className="shrink-0 border-b border-border-light px-xl py-md">
        {/* The row rather than the padded band is what the field covers, so the two stay aligned
            without the overlay having to restate the padding around them. */}
        <div className="relative flex items-center justify-between gap-2">
          <span className="type-heading-sm truncate text-foreground">{title}</span>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0"
                aria-label={t('brandMedia.searchRail')}
                onClick={() => setSearching(true)}
              >
                <SearchIcon />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('brandMedia.searchRail')}</TooltipContent>
          </Tooltip>

          {searching && (
            <div className="absolute inset-0 flex items-center gap-1 bg-card">
              <SearchInput
                size="sm"
                className="min-w-0 flex-1"
                // Appears in response to a click, so taking focus is what was asked for.
                inputRef={(input) => input?.focus()}
                value={search}
                onChange={onSearchChange}
                placeholder={t('brandMedia.searchRail')}
                aria-label={t('brandMedia.searchRail')}
                onKeyDown={(event) => {
                  // A prevented Escape is SearchInput clearing the term; close only once it is empty.
                  if (event.key === 'Escape' && !event.defaultPrevented) close();
                }}
              />
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    className="shrink-0"
                    aria-label={t('brandMedia.closeSearch')}
                    onClick={close}
                  >
                    <XIcon />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('brandMedia.closeSearch')}</TooltipContent>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};
